import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Bot as BotIcon, Radio, Puzzle, Clock, Send, RefreshCw,
  Plus, Trash2, Pencil, ChevronUp, ChevronDown, SkipForward, Loader2, Eye, EyeOff,
  Power, XCircle, CalendarClock, KeyRound, ExternalLink, Hourglass, Download,
  AlertTriangle, CalendarCog,
} from 'lucide-react';
import {
  fetchAccountSummary, fetchAccountBot, createAccountBot, refreshAccountBot,
  verifyAccountBot, enableAccountBot, disableAccountBot, deleteAccountBot,
  getSavedBotCookie,
  setAccountBotMode,
  fetchAccountLive, addAccountLive, removeAccountLive, refreshAccountLive,
  enableAccountLive, disableAccountLive, joinAccountLive, quitAccountLive,
  sendAccountLiveMessage, fetchAccountTimers, addAccountTimer, updateAccountTimer,
  deleteAccountTimer, moveAccountTimer, skipAccountTimer, sendAccountTimerNow,
  setAccountTimerInterval, fetchAccountPlugins, enableAccountPlugin,
  disableAccountPlugin, reloadAccountPlugin, renewAccount, redeemAccount,
  fetchAccountPluginReadme, fetchAccountPluginConfig, updateAccountPluginConfig,
  getAccountBotCookie, uninstallAccountPluginFromAccount, fetchAccountLibrary,
  installAccountPlugin, updateAccountPlugin, updateAllAccountPlugins,
  updateAccountPreferences, expireAccount, updateAccount,
} from '../api/client';
import type {
  AccountSummary, BotInfo, LivestreamInfo, LibraryPlugin, PluginSummary,
  TimerData, TimerMessageItem, BulkGroup, RenewRequest,
  PluginDetail, ConfigFieldSchema,
} from '../api/types';
import { Button } from '../components/Button';
import { Select } from '../components/Select';
import { Switch } from '../components/Switch';
import { HoverTip } from '../components/HoverTip';
import { StatusBadge } from '../components/StatusBadge';
import { ExpiryBadge } from '../components/ExpiryBadge';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CookieHelper } from '../components/CookieHelper';
import { SearchInput, FilterChips } from '../components/ListControls';
import { RenewDialog, AccountInfoDialog } from '../components/AccountDialogs';
import { PluginDrawer } from '../components/PluginDrawer';
import { UninstallDialog } from '../components/UninstallDialog';
import { PluginLogDialog } from '../components/PluginLogDialog';
import { MarqueeText } from '../components/MarqueeText';
import { showToast } from '../hooks/useToast';
import { livePageUrl, parseLiveId } from '../utils/live';

const PERM_NAMES = ['SEND_LIVESTREAM_MESSAGE', 'SEND_PRIVATE_MESSAGE', 'SEND_BACKPACK_GIFT', 'SEND_GIFT', 'EXPOSE_COOKIE'];
const PERM_LABELS: Record<string, string> = {
  SEND_LIVESTREAM_MESSAGE: '发送直播间消息',
  SEND_PRIVATE_MESSAGE: '发送私信',
  SEND_BACKPACK_GIFT: '赠送背包礼物',
  SEND_GIFT: '赠送直售礼物',
  EXPOSE_COOKIE: '查看完整 Cookie',
};

// ================================================================== //
// 概览 Tab
// ================================================================== //

/**
 * 「开播临期提示」开关。
 *
 * 面板管理员与账户本人都能关：提示发在直播间里，是给观众看的，
 * 该不该由账户持有者说了算。点一下即保存，失败回滚。
 */
export function ExpiryNoticeSwitch({ acc }: { acc: AccountSummary }) {
  const [on, setOn] = useState(acc.expiry_notice_enabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setOn(acc.expiry_notice_enabled); }, [acc.expiry_notice_enabled]);

  const toggle = async (next: boolean) => {
    setOn(next);
    setSaving(true);
    try {
      await updateAccountPreferences(acc.id, { expiry_notice_enabled: next });
      showToast('success', next ? '已开启开播临期提示' : '已关闭开播临期提示', '');
    } catch (e: any) {
      setOn(!next);
      showToast('error', '设置失败', e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Switch
      checked={on}
      onChange={toggle}
      loading={saving}
      label="开播临期提示"
      title="开播时若剩余有效期不足 3 天，自动在本直播间发一条续期提醒。默认开启"
    />
  );
}

export function OverviewTab({ acc, onRenew, panelMode }: { acc: AccountSummary; onRenew: (mode: 'days' | 'code' | 'set' | 'permanent' | 'expire') => void; panelMode?: boolean }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="card">
        <div className="card-header"><h3 className="font-semibold">Bot</h3>
          <StatusBadge status={acc.bot_enabled && acc.bot_available ? 'enabled' : 'disabled'}
            label={acc.bot_enabled ? '已启用' : '已停用'} />
        </div>
        <div className="card-body space-y-2 text-sm">
          <p className="text-gray-600 dark:text-gray-300">{acc.bot_name || '未配置 Bot'}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {acc.bot_public ? '使用面板公共 Cookie(账户内不可查看)' : '账户私有 Cookie'}
          </p>
        </div>
      </div>
      <div className="card">
        <div className="card-header"><h3 className="font-semibold">直播间</h3>
          <StatusBadge status={acc.room_connected ? 'online' : 'offline'} label={acc.room_connected ? '已连接' : '未连接'} />
        </div>
        <div className="card-body space-y-2 text-sm">
          <p className="text-gray-600 dark:text-gray-300">{acc.room_name || (acc.room_id ? `房间 ${acc.room_id}` : '未绑定直播间')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">启用状态: {acc.room_enabled ? '已启用' : '已停用'}</p>
        </div>
      </div>
      <div className="card">
        <div className="card-header"><h3 className="font-semibold">插件</h3></div>
        <div className="card-body text-sm text-gray-600 dark:text-gray-300">
          已启用 {acc.enabled_plugin_count} / {acc.plugin_count} 个插件 · 定时消息 {acc.timer_message_count} 条
        </div>
      </div>
      <div className="card">
        <div className="card-header"><h3 className="font-semibold">订阅</h3>
          <ExpiryBadge expiresAt={acc.expires_at} pausedReason={acc.paused_reason} />
        </div>
        <div className="card-body space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {acc.expires_at
              ? `到期时间: ${new Date(acc.expires_at).toLocaleString()}`
              : '永不过期'}
          </p>
          {acc.resume_error && (
            <p className="text-xs text-red-600 dark:text-red-400">{acc.resume_error}</p>
          )}
          {panelMode !== false && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" icon={<CalendarClock className="w-4 h-4" />} onClick={() => onRenew('days')}>续期</Button>
              {/* 其余时长操作集中在这个入口里，打开后可自由切换 */}
              <Button size="sm" variant="secondary" icon={<CalendarCog className="w-4 h-4" />}
                onClick={() => onRenew('set')}>时长管理</Button>
            </div>
          )}
          <div className="flex items-start justify-between gap-3 border-t border-gray-100 dark:border-gray-700 pt-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-700 dark:text-gray-200">开播临期提示</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                开播时若剩余不足 3 天，自动在直播间发一条续期提醒。默认开启
              </p>
            </div>
            <ExpiryNoticeSwitch acc={acc} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ================================================================== //
// 直播间 Tab(单房间)
// ================================================================== //

/**
 * 演示用的虚拟直播间。只在「操作指引」浮着、且账户还没绑直播间时喂给 LiveTab ——
 * 没有它，「连接 / 断开」那一步对空账户就是死的（页面上压根没有房间卡片可指）。
 *
 * 三条底线：
 *   - 封面与头像留空，免得拿着假 URL 去请求图片代理（裂图 + 白跑一趟），
 *     卡片会自动退回默认头像;
 *   - 所有动作都不走 API（见 `act` 与「启用 / 停用」的 demo 分支），
 *     怎么点都作用不到账户上;
 *   - 卡片顶部必须有显眼的「演示用」标记 —— **假数据得一眼看得出是假的**，
 *     否则用户会以为自己真绑了个直播间。
 */
const makeDemoRoom = (): LivestreamInfo => ({
  live_id: 0,
  room_name: '演示直播间',
  room_description: '这是操作指引生成的临时虚拟直播间，只在指引期间出现。',
  score: 1280,
  online_count: 12,
  creator_name: '演示主播',
  creator_id: 0,
  creator_is_online: true,
  is_connected: false,
  enabled: false,
  medal_name: '演示勋章',
  medal_level: 3,
  cover_url: '',
  creator_avatar: '',
  creator_intro: '虚拟主播，仅在操作指引里出现，不是真实直播间。',
  is_streaming: true,
});

/**
 * 演示用的插件清单。指引期间顶掉账户真实的插件列表，理由和虚拟直播间一样：
 * 账户可能一个插件都没装，「插件管理」那一步就没有卡片可指；而「插件主页」
 * 那一步还要求清单里**至少有一个 has_ui 的插件**，真实账户不一定有。
 *
 * ⚠️ 假数据必须一眼看得出是假的：作者统一写「演示插件」、plugin_id 走 demo.* 前缀，
 * 页头另挂一条横幅。**别**把它们改成看起来像真插件的样子。
 */
const DEMO_PLUGINS: PluginSummary[] = [
  {
    // 带插件主页的那个排在**第一个**：卡片上「插件主页」「详情」「配置」三个锚点
    // 都取 DOM 里第一个命中的元素，排第一才能保证指引四步指的是同一张卡
    name: 'song_list', plugin_id: 'demo.song-list', author: '演示插件', version: '1.2.0',
    display_name: '点歌', short_desc: '观众发「点歌 歌名」即可点播',
    desc: '观众发「点歌 歌名」即可点播',
    enabled: true, has_config: true, has_readme: true, has_changelog: true, has_ui: true,
  },
  {
    name: 'welcome', plugin_id: 'demo.welcome', author: '演示插件', version: '1.0.0',
    display_name: '欢迎插件', short_desc: '新观众进入直播间时自动发送欢迎语',
    desc: '新观众进入直播间时自动发送欢迎语',
    enabled: true, has_config: true, has_readme: true, has_changelog: false, has_ui: false,
  },
  {
    name: 'gift_thanks', plugin_id: 'demo.gift-thanks', author: '演示插件', version: '0.9.0',
    display_name: '礼物答谢', short_desc: '收到礼物时自动致谢',
    desc: '收到礼物时自动致谢',
    enabled: false, has_config: false, has_readme: true, has_changelog: false, has_ui: false,
  },
];

/** 演示用的插件库清单（插件库页用）。字段比插件清单多几个库专有的。 */
const DEMO_LIBRARY: LibraryPlugin[] = [
  {
    name: 'song_list', plugin_id: 'demo.song-list', author: '演示插件', version: '1.2.0',
    display_name: '点歌', short_desc: '观众发「点歌 歌名」即可点播',
    desc: '观众发「点歌 歌名」即可点播',
    has_config: true, has_readme: true, has_changelog: true, has_ui: true,
    is_default: false, used_by_accounts: [], installed: false,
  },
  {
    name: 'welcome', plugin_id: 'demo.welcome', author: '演示插件', version: '1.0.0',
    display_name: '欢迎插件', short_desc: '新观众进入直播间时自动发送欢迎语',
    desc: '新观众进入直播间时自动发送欢迎语',
    has_config: true, has_readme: true, has_changelog: false, has_ui: false,
    is_default: false, used_by_accounts: [], installed: true,
  },
  {
    name: 'gift_thanks', plugin_id: 'demo.gift-thanks', author: '演示插件', version: '0.9.0',
    display_name: '礼物答谢', short_desc: '收到礼物时自动致谢',
    desc: '收到礼物时自动致谢',
    has_config: false, has_readme: true, has_changelog: false, has_ui: false,
    is_default: true, used_by_accounts: [], installed: true,
  },
];

/** 指引要引导用户装的那个演示插件 —— 带插件主页，装完插件页那几步才有东西可点 */
const DEMO_INSTALL_NAME = 'song_list';
/** 一开始就「装着」的演示插件（装了的才出现在插件页列表里） */
const DEMO_INITIAL_INSTALLED = ['welcome', 'gift_thanks'];

// ---- 演示态的「已安装」集合 ----
// 插件库页点安装、插件页读列表，是**两条路由、同一时刻只挂载一个**，
// 所以这份进度不能放在组件里 —— 一换页就断了，用户会看到「刚装的插件不见了」。
// 插件库页每次挂载都会重置（见 LibraryTab），保证重看指引时从头演一遍。
let demoInstalled = new Set<string>(DEMO_INITIAL_INSTALLED);
const demoInstallListeners = new Set<() => void>();
const subscribeDemoInstall = (l: () => void) => {
  demoInstallListeners.add(l);
  return () => { demoInstallListeners.delete(l); };
};
function setDemoInstalled(next: Set<string>) {
  demoInstalled = next;
  demoInstallListeners.forEach((l) => l());
}
function useDemoInstalled(): Set<string> {
  return useSyncExternalStore(subscribeDemoInstall, () => demoInstalled);
}

/**
 * 清掉演示进度（哪些演示插件被「装」过）。指引结束（完成 / 跳过）时由
 * GuideOverlay 调用 —— 这份状态是模块级的，不主动清就会留到下一轮。
 */
export function resetPluginDemo() {
  setDemoInstalled(new Set(DEMO_INITIAL_INSTALLED));
}

/**
 * 演示用的插件详情。指引期间抽屉不再按插件名去后端拉 —— 演示插件的名字
 * （demo.song-list）在账户里根本不存在，拉就是 404。这里一份假数据喂满
 * 「基本信息」与「配置」两个 tab。
 *
 * ⚠️ 配置 schema 是按「点歌」写的固定值。指引的「配置」那一步指的就是点歌
 * 那张卡，换成别的演示插件会显得文不对题 —— 真要加别的，这里得按名字分支。
 */
export function makeDemoPluginDetail(name: string): PluginDetail {
  const meta = DEMO_LIBRARY.find((p) => p.name === name) ?? DEMO_LIBRARY[0];
  return {
    name: meta.name,
    plugin_id: meta.plugin_id,
    author: meta.author,
    version: meta.version,
    display_name: meta.display_name,
    short_desc: meta.short_desc,
    desc: meta.desc,
    repo: null,
    enabled: true,
    has_config: meta.has_config,
    has_readme: meta.has_readme,
    has_changelog: meta.has_changelog,
    handlers: [
      { method_name: 'on_live_message', event_type: 'LiveMessageEvent' },
      { method_name: 'on_live_gift', event_type: 'LiveGiftEvent' },
    ],
    permissions: { SEND_LIVESTREAM_MESSAGE: true },
    config_schema: DEMO_CONFIG_SCHEMA,
    config_values: DEMO_CONFIG_VALUES,
    ui_schema: null,
  };
}

const DEMO_CONFIG_SCHEMA: Record<string, ConfigFieldSchema> = {
  max_queue: { type: 'int', default: 5, description: '每人最多排队几首', group: '常用' },
  allow_duplicate: { type: 'bool', default: false, description: '允许同一首歌重复排队', group: '常用' },
  reply_text: { type: 'string', default: '已为你点播《{song}》', description: '点歌成功后的回复话术', group: '高级' },
};
const DEMO_CONFIG_VALUES: Record<string, unknown> = {
  max_queue: 5, allow_duplicate: false, reply_text: '已为你点播《{song}》',
};

/** 用了假数据的页面顶部都得挂一条 —— 别让用户以为这是自己账户的真实状态 */
function DemoBanner({ what }: { what: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 text-xs rounded-lg
                    bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300
                    border border-amber-200 dark:border-amber-800/60">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      {what}
    </div>
  );
}

export function LiveTab({ acc, demo = false, onAccountReload }: {
  acc: AccountSummary;
  demo?: boolean;
  /** 绑定关系变化后刷新账户概要 —— `acc` 不会自己更新，见 `actBinding` */
  onAccountReload?: () => void;
}) {
  const [room, setRoom] = useState<LivestreamInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [liveIdInput, setLiveIdInput] = useState('');
  const [switching, setSwitching] = useState(false); // 更换直播间输入态
  const [msgText, setMsgText] = useState('');
  const [msgPriority, setMsgPriority] = useState(0);

  const load = useCallback(async () => {
    // 演示态：直接把假房间塞进去，**一个请求都不发**（账户本来就没绑直播间）
    if (demo) { setRoom(makeDemoRoom()); setLoading(false); return; }
    try {
      const data = await fetchAccountLive(acc.id);
      setRoom(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [acc.id, demo]);

  useEffect(() => {
    load();
    // 演示态不轮询：每 8 秒重拉一次会把用户刚点出来的「已连接」冲回初始值
    if (demo) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, demo]);

  const act = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    // 演示态一律不碰 API —— 虚拟房间上的刷新 / 更换 / 解绑 / 发弹幕都不该作用到账户上。
    //（「启用 / 停用」也不走这里，它有自己的 demo 分支）
    if (demo) return;
    setProcessing(key);
    try {
      await fn();
      showToast('success', okMsg, '');
      load();
    } catch (e: any) {
      showToast('error', '操作失败', e.message);
    } finally { setProcessing(''); }
  };

  const btn = (key: string, disabled = false) => processing === key || disabled;

  /**
   * 绑定类操作：跑完动作后**还要刷新账户概要**。
   *
   * `acc` 是账户详情页挂载时取的一次快照，此后不再自动更新。解绑之后它仍带着旧的
   * `room_id`，而直播间接口此时返回的是 `null` —— 渲染分支
   * `room === null && acc.room_id != null` 会被读成「已绑定但加载失败」，
   * 于是刚删掉的直播间又弹出一张「房间已不存在」的错误卡片。
   */
  const actBinding = (key: string, fn: () => Promise<unknown>, okMsg: string) =>
    act(key, async () => { await fn(); await onAccountReload?.(); }, okMsg);

  /** 重试加载已绑定但加载不出来的直播间（同 ID 重新提交，服务端已放开该校验）。 */
  const retryBoundRoom = () => {
    const rid = acc.room_id;
    if (rid == null) return;
    return actBinding('retry', () => addAccountLive(acc.id, rid), '直播间信息已重新加载');
  };

  /**
   * 把输入框内容解析成直播间 id。
   * 既接受裸 ID，也接受粘贴的直播间链接；解析失败抛错，由 act 统一弹提示。
   */
  const resolveLiveInput = () => {
    const id = parseLiveId(liveIdInput);
    if (id === null) {
      throw new Error('请填写直播间 ID，或粘贴形如 https://fm.missevan.com/live/869198039 的链接');
    }
    return id;
  };

  /**
   * 未绑定时先摆出来的绑定入口。演示态也要用它 —— 指引第一步（绑定直播间）
   * 指的就是这里，不能因为进了演示就把真实入口抽掉。
   */
  const bindCard = (
    <div className="card" data-guide="lv-bind">
      <div className="card-body">
        <div className="flex gap-2">
          <input value={liveIdInput} onChange={(e) => setLiveIdInput(e.target.value)}
            className="input flex-1" placeholder="输入直播间 ID 或粘贴直播间链接" />
          <Button loading={processing === 'add'} disabled={btn('add')}
            onClick={() => actBinding('add', () => addAccountLive(acc.id, resolveLiveInput()), '直播间已绑定')}>
            绑定
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {loading && !room ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
      ) : room === null && acc.room_id == null ? (
        bindCard
      ) : room === null ? (
        /* 已绑定直播间但加载不出来（不存在 / 已被封禁 / 已注销）——
           不能当作「未绑定」直接给输入框，否则用户看不出绑定还在、只会以为没绑 */
        <div className="card" data-guide="lv-bind">
          <div className="card-body space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-gray-700 dark:text-gray-200">
                  绑定的直播间 <span className="font-mono font-semibold">{acc.room_id}</span> 信息获取失败，未能加载。
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  常见原因：房间已不存在、被平台封禁，或主播已注销。
                  若确认该房间可正常访问，可尝试刷新；否则请重新绑定。
                </p>
              </div>
            </div>
            {switching ? (
              <div>
                <div className="flex gap-2">
                  <input value={liveIdInput} onChange={(e) => setLiveIdInput(e.target.value)}
                    className="input flex-1" placeholder="输入新的直播间 ID..." inputMode="numeric" autoFocus />
                  <Button loading={processing === 'switch'} disabled={btn('switch')}
                    onClick={() => actBinding('switch', () => addAccountLive(acc.id, resolveLiveInput()).then(() => {
                      setSwitching(false);
                      setLiveIdInput('');
                    }), '直播间已更换')}>
                    确认更换
                  </Button>
                  <Button variant="ghost" disabled={btn('switch')}
                    onClick={() => { setSwitching(false); setLiveIdInput(''); }}>
                    取消
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">更换后新直播间需手动启用</p>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="secondary" icon={<RefreshCw />}
                  loading={processing === 'retry'} disabled={btn('retry')}
                  onClick={retryBoundRoom}>
                  重试加载
                </Button>
                <Button variant="ghost" disabled={btn('retry')}
                  onClick={() => { setSwitching(true); setLiveIdInput(''); }}>
                  重新绑定
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
        {/* 演示态：账户没绑直播间，先摆真实绑定入口（上面那步讲的就是它），
            下面再补一张虚拟房间 —— 否则「连接 / 断开」那一步对空账户是死的 */}
        {demo && bindCard}
        {/* 更换直播间输入行 */}
        {switching && (
          <div className="card">
            <div className="card-body">
              <div className="flex gap-2">
                <input value={liveIdInput} onChange={(e) => setLiveIdInput(e.target.value)}
                  className="input flex-1" placeholder="输入新的直播间 ID..." inputMode="numeric" autoFocus />
                <Button loading={processing === 'switch'} disabled={btn('switch')}
                  onClick={() => actBinding('switch', () => addAccountLive(acc.id, resolveLiveInput()).then(() => {
                    setSwitching(false);
                    setLiveIdInput('');
                  }), '直播间已更换')}>
                  确认更换
                </Button>
                <Button variant="ghost" disabled={btn('switch')}
                  onClick={() => { setSwitching(false); setLiveIdInput(''); }}>
                  取消
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2">更换后原直播间自动断开,新直播间需手动启用</p>
            </div>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {/* 演示标记：假房间必须一眼看得出来的假的，否则用户会以为自己真绑了直播间 */}
          {demo && (
            <div className="flex items-center gap-2 px-5 py-2.5 text-xs
                            bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300
                            border-b border-amber-200 dark:border-amber-800/60">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              演示用虚拟直播间「{room.room_name}」 —— 这里怎么点都不会影响你的账户
            </div>
          )}
          {/* 封面 */}
          {room.cover_url && (
            <div className="relative h-40 sm:h-52 bg-surface-100 dark:bg-surface-900">
              <img
                src={`/api/proxy/image?url=${encodeURIComponent(room.cover_url)}`}
                alt="cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              {/* 开播状态 */}
              <div className="absolute top-3 left-3">
                <span className={
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium backdrop-blur ' +
                  (room.is_streaming
                    ? 'bg-red-500/90 text-white'
                    : 'bg-gray-900/70 text-gray-200')
                }>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${room.is_streaming ? 'bg-white animate-pulse' : 'bg-gray-400'}`} />
                  {room.is_streaming ? '开播中' : '未开播'}
                </span>
              </div>
              {/* 房间名 */}
              <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2">
                {/* 房间名过长就滚动显示，不再被 truncate 截掉 */}
                <MarqueeText className="min-w-0 flex-1 font-semibold text-lg text-white drop-shadow"
                  text={`${room.room_name} (${room.live_id})`} />
                <a href={livePageUrl(room.live_id)} target="_blank" rel="noopener noreferrer"
                  title="在猫耳打开直播间"
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg
                             bg-black/30 hover:bg-black/50 text-white text-xs
                             backdrop-blur-sm transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" />
                  打开
                </a>
              </div>
            </div>
          )}

          <div className="p-5 space-y-4">
            {/* 主播信息 */}
            <div className="flex items-start gap-3">
              {room.creator_avatar ? (
                <img
                  src={`/api/proxy/image?url=${encodeURIComponent(room.creator_avatar)}`}
                  alt="avatar"
                  className="w-12 h-12 rounded-full object-cover shrink-0 bg-white border border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
                  <BotIcon className="w-6 h-6 text-primary-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 dark:text-white">{room.creator_name || '未知主播'}</p>
                  <StatusBadge status={room.creator_is_online ? 'online' : 'offline'}
                    label={room.creator_is_online ? '主播在线' : '主播离线'} />
                  <StatusBadge status={room.is_connected ? 'online' : 'offline'}
                    label={room.is_connected ? '已连接' : '未连接'} />
                </div>
                {room.creator_intro && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{room.creator_intro}</p>
                )}
              </div>
            </div>

            {/* 房间简介 */}
            {room.room_description && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                <p className="text-[10px] text-gray-400 mb-0.5">直播间简介</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{room.room_description}</p>
              </div>
            )}

            {/* 统计 */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-3">
                <p className="text-gray-400 text-xs">热度</p>
                <p className="font-semibold text-lg mt-0.5">{room.score >= 0 ? room.score : '-'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-3">
                <p className="text-gray-400 text-xs">在线人数</p>
                <p className="font-semibold text-lg mt-0.5">{room.online_count}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 py-3">
                <p className="text-gray-400 text-xs">粉丝勋章</p>
                <p className="font-semibold text-lg mt-0.5 truncate">{room.medal_name || '-'}</p>
              </div>
            </div>

            {/* 操作:启用即自动进入,停用即断开。
                指引「连接 / 断开」那一步指的是**这一个按钮**，不是整行 —— 整行里还有
                「更换直播间」「解绑」，锚点落在整行上的话，那两个会跟着一起漏出可点区 */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={room.enabled ? 'secondary' : 'success'}
                data-guide="lv-control"
                // 只有虚拟房间才允许在指引里点。真实房间没有这个声明，照样挡住 ——
                // 那里按一下「停用」就真的断开了
                data-guide-clickable={demo ? '1' : undefined}
                loading={processing === 'enable'} disabled={btn('enable')}
                onClick={() => {
                  // 演示态只切本地状态：不请求、不落盘，纯粹给用户按着玩
                  if (demo) {
                    setRoom((r) => (r ? { ...r, enabled: !r.enabled, is_connected: !r.enabled } : r));
                    return;
                  }
                  act('enable', () => room.enabled
                    ? disableAccountLive(acc.id)
                    : enableAccountLive(acc.id), room.enabled ? '直播间已停用' : '直播间已启用并自动进入');
                }}>
                {room.enabled ? '停用' : '启用'}
              </Button>
              <Button size="sm" variant="ghost" icon={<RefreshCw className="w-4 h-4" />}
                loading={processing === 'refresh'} disabled={btn('refresh')}
                onClick={() => act('refresh', () => refreshAccountLive(acc.id), '已刷新')} />
              {/* 已绑定状态下，输入框本身是折叠的 —— 指到这里，用户看到的
                  就是「换绑入口」，与未绑定时指向输入卡是同一个意思 */}
              <Button size="sm" variant="secondary" icon={<Radio className="w-4 h-4" />}
                data-guide="lv-bind"
                loading={processing === 'switch'} disabled={btn('switch')}
                onClick={() => { setSwitching(!switching); setLiveIdInput(''); }}>
                更换直播间
              </Button>
              <Button size="sm" variant="ghost" icon={<Trash2 className="w-4 h-4 text-red-500" />}
                loading={processing === 'remove'} disabled={btn('remove')}
                onClick={() => actBinding('remove', () => removeAccountLive(acc.id), '已解除绑定')} />
            </div>

            {/* 发弹幕 */}
            <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
              <input value={msgText} onChange={(e) => setMsgText(e.target.value)}
                className="input flex-1" placeholder="输入要发送的弹幕..." />
              <Select
                value={String(msgPriority)}
                onChange={(v) => setMsgPriority(Number(v))}
                options={[{ value: '0', label: '普通' }, { value: '1', label: '优先' }]}
                ariaLabel="弹幕优先级"
                className="w-28 shrink-0"
              />
              <Button icon={<Send className="w-4 h-4" />} loading={processing === 'send'} disabled={btn('send')}
                onClick={() => act('send', () => sendAccountLiveMessage(acc.id, msgText, msgPriority).then(() => setMsgText('')), '消息已发送')}>
                发送
              </Button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

// ================================================================== //
// ================================================================== //
// Bot Tab(模式选择 + Bot 卡片)
// ================================================================== //
// ================================================================== //
// Bot Tab(模式选择 + Bot 卡片)
// ================================================================== //

export function BotTab({ acc, onAccountChanged }: {
  acc: AccountSummary;
  onAccountChanged?: () => void;
}) {
  const [bot, setBot] = useState<BotInfo | null>(null);
  const [mode, setMode] = useState<'public' | 'private'>(acc.bot_public ? 'public' : 'private');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [cookie, setCookie] = useState('');
  const [perms, setPerms] = useState<string[]>(['SEND_LIVESTREAM_MESSAGE']);
  const [showCookie, setShowCookie] = useState(false);
  const [viewedCookie, setViewedCookie] = useState('');
  const [confirmPublic, setConfirmPublic] = useState(false);

  const load = useCallback(async () => {
    try {
      setBot(await fetchAccountBot(acc.id));
    } catch { /* 未配置 */ }
    finally { setLoading(false); }
  }, [acc.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setMode(acc.bot_public ? 'public' : 'private'); }, [acc.bot_public]);

  /** 返回是否成功，便于调用方只在成功时推进本地状态。 */
  const act = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setProcessing(key);
    try {
      await fn();
      showToast('success', okMsg, '');
      load();
      onAccountChanged?.();
      return true;
    } catch (e: any) {
      showToast('error', '操作失败', e.message);
      return false;
    } finally { setProcessing(''); }
  };

  const switchMode = (m: 'public' | 'private') => {
    if (m === mode) return;
    if (m === 'public') {
      // 切公共会立刻换掉正在跑的 Bot，先确认。自有 Cookie 不会被删（见下方）。
      setConfirmPublic(true);
    } else {
      // 切回自定义必须由用户明确给出一串 Cookie 并保存（见下方表单），
      // 这里只切本地视图，不发请求
      setMode('private');
      showToast('success', '请在下方填入自定义 Cookie 并保存', '');
    }
  };

  const doSwitchPublic = async () => {
    // 后端未配置公共 Cookie 时会失败，act 返回 false，本地状态保持不变
    const ok = await act('mode', () => setAccountBotMode(acc.id, 'public'), '已切换为公共 Cookie');
    if (ok) setMode('public');
    setConfirmPublic(false);
  };

  /** 把切公共时留存的 Cookie 填进输入框 —— 用户仍需点保存才会真正切回。 */
  const fillSavedCookie = async () => {
    try {
      const r = await getSavedBotCookie(acc.id);
      if (!r.cookie) { showToast('error', '没有留存的自定义 Cookie', ''); return; }
      setCookie(r.cookie);
      showToast('info', '已填入上次保存的 Cookie', '确认后点下方按钮保存');
    } catch (e: any) {
      showToast('error', '读取失败', e.message);
    }
  };

  const savePrivateCookie = () => {
    if (!cookie.trim()) {
      showToast('error', '请输入 Cookie', '');
      return;
    }
    act('mode', () => setAccountBotMode(acc.id, 'private', cookie.trim(), perms), '已使用自定义 Cookie')
      .then(() => { setCookie(''); setMode('private'); });
  };

  const togglePerm = (p: string) => {
    setPerms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* 模式选择 */}
      <div className="card">
        <div className="card-body">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Cookie 来源</label>
          <div className="flex gap-2">
            {(['public', 'private'] as const).map((m) => (
              <button key={m} onClick={() => switchMode(m)}
                // 指引先指「自定义 Cookie」这一步 —— 换 Cookie 得先切过来，
                // 而且这张卡始终在，比下面的 Cookie 表单卡可靠（公共模式没有那张）
                data-guide={m === 'private' ? 'bt-mode' : undefined}
                // 允许指引里点它：切到自定义**本来就只切本地视图、不发请求**
                // （真正换 Cookie 是下面表单里点保存，那个按钮指引期间照旧挡住）。
                // 「公共 Cookie」那个按钮**不能**放行 —— 它会弹二次确认框，而确认框
                // 在蒙版之下（z-80 < 85），弹出来会既看不见也点不到，看着像卡死
                data-guide-clickable={m === 'private' ? '1' : undefined}
                className={
                  'flex-1 h-9 rounded-lg border text-sm transition-colors ' +
                  (mode === m
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700')
                }>
                {m === 'public' ? '公共 Cookie' : '自定义 Cookie'}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {mode === 'public'
              ? '使用面板统一配置的公共 Cookie，账户内不可查看。'
              : '使用本账户独立的 Cookie，可查看与随时更换。'}
          </p>
        </div>
      </div>

      {/* Bot 卡片 */}
      {bot ? (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2 min-w-0">
              {bot.icon_url ? (
                <img
                  src={`/api/proxy/image?url=${encodeURIComponent(bot.icon_url)}`}
                  alt="avatar"
                  className="w-8 h-8 rounded-full object-cover shrink-0 bg-white"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center shrink-0">
                  <BotIcon className="w-4 h-4 text-primary-500" />
                </div>
              )}
              <h3 className="font-semibold truncate">{bot.name || 'Bot'}</h3>
            </div>
            <StatusBadge status={bot.enabled && bot.available ? 'enabled' : 'disabled'}
              label={bot.enabled ? '已启用' : '已停用'} />
          </div>
          <div className="card-body space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              用户 ID: {bot.user_id} · 可用: {bot.available ? '是' : '否'}
            </p>
            {bot.introduction && (
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{bot.introduction}</p>
            )}
            <div className="flex flex-wrap gap-1">
              {PERM_NAMES.filter((p) => bot.permissions.includes(p)).map((p) => (
                <span key={p} className="badge badge-blue">{p}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <Button size="sm" variant={bot.enabled ? 'secondary' : 'success'}
                loading={processing === 'toggle'} disabled={processing === 'toggle'}
                onClick={() => act('toggle', () => bot.enabled ? disableAccountBot(acc.id) : enableAccountBot(acc.id),
                  bot.enabled ? 'Bot 已停用' : 'Bot 已启用')}>
                {bot.enabled ? '停用' : '启用'}
              </Button>
              <Button size="sm" variant="ghost" icon={<RefreshCw className="w-4 h-4" />}
                loading={processing === 'refresh'} disabled={processing === 'refresh'}
                onClick={() => act('refresh', () => refreshAccountBot(acc.id), '已刷新')}>刷新</Button>
              {mode === 'private' && (
                <>
                  <Button size="sm" variant="ghost" icon={<Eye className="w-4 h-4" />}
                    loading={processing === 'cookie'} disabled={processing === 'cookie'}
                    onClick={() => act('cookie', async () => {
                      const r = await getAccountBotCookie(acc.id);
                      setViewedCookie(r.cookie);
                      setShowCookie(true);
                    }, '')}>查看 Cookie</Button>
                  <Button size="sm" variant="ghost" icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    loading={processing === 'del'} disabled={processing === 'del'}
                    onClick={() => act('del', () => deleteAccountBot(acc.id), 'Bot 已删除')}>删除</Button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">
              {mode === 'public'
                ? '尚未使用公共 Cookie 连接（可能面板尚未配置公共 Cookie）。'
                : '尚未配置 Bot，请在下方输入自定义 Cookie。'}
            </p>
          </div>
        </div>
      )}

      {/* 自助获取 Cookie —— 与自定义 Cookie 表单分开一张卡：
          取 Cookie 是「怎么拿到」，下面是「拿到之后怎么填 + 给什么权限」 */}
      {mode === 'private' && (
        <div className="card">
          <div className="card-header"><h3 className="font-semibold">获取 Cookie</h3></div>
          <div className="card-body">
            <CookieHelper accountId={acc.id} onDone={onAccountChanged} />
          </div>
        </div>
      )}

      {/* 自定义 Cookie 表单(含完整权限设置)。
          data-guide 指到这里：公共 Cookie 模式下这张卡不渲染，指引那一步会
          退化成「只有文字、没有高亮」—— 可以接受，公共 Cookie 本来就换不了 */}
      {mode === 'private' && (
        <div className="card" data-guide="bt-cookie">
          <div className="card-header"><h3 className="font-semibold">{bot ? '更换 Cookie' : '配置自定义 Cookie'}</h3></div>
          <div className="card-body space-y-3">
            <textarea value={cookie} onChange={(e) => setCookie(e.target.value)} rows={3}
              className="input w-full font-mono text-xs" placeholder="粘贴 Missevan Cookie..." />
            {/* 切到公共 Cookie 时那一串会被留存下来，这里给出取回入口 ——
                否则用户得重新去浏览器里翻一遍 */}
            {bot?.has_saved_cookie && (
              <button type="button" onClick={fillSavedCookie}
                className="inline-flex items-center min-h-8 py-1 text-xs text-primary-600
                  dark:text-primary-400 hover:underline">
                填入上次保存的 Cookie
              </button>
            )}
            <div>
              <p className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Bot 权限设置(自定义 Cookie 可完整设置)
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {PERM_NAMES.map((p) => (
                  <label key={p}
                    className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 cursor-pointer text-sm">
                    <input type="checkbox" checked={perms.includes(p)}
                      onChange={() => togglePerm(p)}
                      className="w-4 h-4 accent-primary-600" />
                    <span className="flex-1">
                      <span className="block font-mono text-xs">{p}</span>
                      <span className="block text-[10px] text-gray-400">{PERM_LABELS[p] || p}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <Button loading={processing === 'mode'} disabled={processing === 'mode'}
              onClick={savePrivateCookie}>
              {bot ? '保存并使用此 Cookie' : '创建 Bot'}
            </Button>
          </div>
        </div>
      )}

      {/* 切公共 Cookie 的二次确认 —— 会立刻换掉正在跑的 Bot */}
      <ConfirmDialog
        open={confirmPublic}
        title="切换为公共 Cookie"
        message={`账户「${acc.name}」将改用面板统一配置的公共 Cookie，Bot 权限会降级为仅发送直播间消息。`
          + '你当前的自定义 Cookie 会保留下来，之后想切回可以一键填回。'}
        confirmLabel="切换"
        loading={processing === 'mode'}
        onConfirm={doSwitchPublic}
        onCancel={() => setConfirmPublic(false)}
      />

      {/* Cookie 查看弹框 */}
      {showCookie && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCookie(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-5">
            <h3 className="font-semibold mb-3">Cookie 信息</h3>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded-lg">{viewedCookie}</pre>
            <div className="flex justify-end mt-4">
              <Button variant="ghost" size="sm" onClick={() => setShowCookie(false)}>关闭</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 定时消息 Tab(v1.0.1 布局:间隔卡 + 直播间行 + 消息卡)
// ================================================================== //

export function TimerTab({ acc }: { acc: AccountSummary }) {
  const [data, setData] = useState<TimerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [intervalInput, setIntervalInput] = useState('60');
  const [editor, setEditor] = useState<{ messageId?: string; text: string } | null>(null);
  const [editorSaving, setEditorSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TimerMessageItem | null>(null);
  // 消息来源筛选（展示用，不影响轮转顺序与指针）
  const [sourceFilter, setSourceFilter] = useState<'all' | 'normal' | 'plugin'>('all');
  // 本地 1s 倒计时:每秒重渲染,减去自上次拉取以来的流逝秒数
  const [, setTick] = useState(0);
  const loadedAtRef = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const d = await fetchAccountTimers(acc.id);
      setData(d);
      setIntervalInput(String(d.interval));
      loadedAtRef.current = Date.now();
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [acc.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const ticker = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(t); clearInterval(ticker); };
  }, [load]);

  const act = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setProcessing(key);
    try {
      await fn();
      showToast('success', okMsg, '');
      load();
    } catch (e: any) {
      showToast('error', '操作失败', e.message);
    } finally { setProcessing(''); }
  };

  if (loading && !data) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  const messages = data?.global ?? [];
  const position = data?.rooms?.[0]?.position ?? 0;
  const pointer = messages.length > 0 ? position % messages.length : 0;
  // 筛选只影响展示：轮转位置一律用后端给的 m.index，
  // 否则筛选后下标改变会让「▶ 即将执行」和按钮可用性错位
  const visible = sourceFilter === 'all'
    ? messages
    : messages.filter((m) => (m.source ?? 'normal') === sourceFilter);
  const pluginCount = messages.filter((m) => m.source === 'plugin').length;
  const normalCount = messages.length - pluginCount;
  // 本地流逝秒数:倒计时每秒递减(30s 静默重同步校正漂移)
  const elapsed = Math.floor((Date.now() - loadedAtRef.current) / 1000);
  const liveCountdown = (s: number) => Math.max(0, s - elapsed);

  const fmtCountdown = (s: number) => {
    if (s >= 60) return `${Math.floor(s / 60)}分${s % 60}秒`;
    return `${s}秒`;
  };

  const saveEditor = async () => {
    if (!editor || !editor.text.trim()) return;
    if (editor.messageId) {
      // 客户端兜底：插件托管的消息不可编辑（服务端另有 400 拦截）
      const target = messages.find((m) => m.message_id === editor.messageId);
      if (target?.source === 'plugin') {
        showToast('error', '无法编辑', '插件消息由插件托管');
        setEditor(null);
        return;
      }
    }
    setEditorSaving(true);
    try {
      if (editor.messageId) {
        await updateAccountTimer(acc.id, editor.messageId, editor.text.trim());
        showToast('success', '消息已更新', '');
      } else {
        await addAccountTimer(acc.id, editor.text.trim());
        showToast('success', '已添加定时消息', '');
      }
      setEditor(null);
      load();
    } catch (e: any) {
      showToast('error', '保存失败', e.message);
    } finally { setEditorSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      {/* 页面头部 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">定时消息队列</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {messages.length} 条消息 · 按轮转间隔发送
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" icon={<RefreshCw className="w-4 h-4" />}
            loading={processing === 'refresh'} disabled={processing === 'refresh'}
            onClick={() => act('refresh', () => load(), '已刷新')}>
            刷新
          </Button>
          <Button variant="primary" icon={<Plus className="w-4 h-4" />}
            onClick={() => setEditor({ text: '' })}>
            添加消息
          </Button>
        </div>
      </div>

      {/* 发送间隔 */}
      <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-5 py-4">
        <TimerIcon className="w-5 h-5 text-primary-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">发送间隔</p>
          <p className="text-[10px] text-gray-400">修改后实时生效，不重置执行位置指针</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input type="number" min={1} value={intervalInput}
            onChange={(e) => setIntervalInput(e.target.value)}
            className="w-24 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-primary-500" />
          <span className="text-sm text-gray-500">秒</span>
          <Button size="sm" loading={processing === 'interval'} disabled={processing === 'interval'}
            onClick={() => act('interval', () => setAccountTimerInterval(acc.id, Number(intervalInput)), '间隔已更新')}>
            保存
          </Button>
        </div>
      </div>

      {/* 直播间 */}
      <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-5 py-4">
        <RadioIcon className="w-5 h-5 text-emerald-500 shrink-0" />
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 shrink-0">直播间</span>
        <div className="flex-1 max-w-xs">
          <span className="text-xs text-gray-600 dark:text-gray-300">
            {acc.room_name ? `[${acc.room_id}] ${acc.room_name}` : (acc.room_id ? `直播间 ${acc.room_id}` : '未绑定直播间')}
          </span>
        </div>
      </div>

      {/* 消息卡 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
          <RadioIcon className="w-4 h-4 text-emerald-500" />
          {acc.room_name ? `${acc.room_name}（${acc.room_id}）` : `直播间 ${acc.room_id ?? '未绑定'}`} 定时消息
          <span className="text-xs text-gray-400 font-normal">（{messages.length} 条 · 指针 #{messages.length ? pointer + 1 : 0}）</span>
        </h2>
        {messages.length > 0 && (
          <div className="flex items-center gap-1 mb-3">
            {([
              ['all', `全部 ${messages.length}`],
              ['normal', `普通 ${normalCount}`],
              ['plugin', `插件 ${pluginCount}`],
            ] as const).map(([key, label]) => (
              <button key={key} onClick={() => setSourceFilter(key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  sourceFilter === key
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {messages.length === 0 ? (
          <p className="text-center text-gray-400 py-6 text-sm">暂无定时消息</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-gray-400 py-6 text-sm">
            没有符合「{sourceFilter === 'plugin' ? '插件' : '普通'}」筛选的消息
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map((m) => {
              // 插件托管的消息：编辑/删除/移动由服务端拒绝，这里同步置灰
              const locked = m.source === 'plugin';
              return (
              <div key={m.message_id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-sm transition-shadow">
                <span className="text-xs font-bold text-gray-400 w-8 text-center shrink-0">#{m.index + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="overflow-hidden text-sm text-gray-800 dark:text-gray-200">
                    <MarqueeText text={m.message} />
                  </div>
                  <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                    直播间 {acc.room_id ?? '-'} · {m.message_id}
                    {m.index === pointer && (
                      <span className="ml-2 text-primary-500 font-semibold">▶ 即将执行</span>
                    )}
                    {locked && (
                      <span
                        title={`由插件 ${m.plugin_name ?? ''} 托管，不可编辑/删除/移动`}
                        className="ml-2 px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 font-sans font-semibold">
                        插件
                      </span>
                    )}
                  </p>
                </div>
                <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <TimerIcon className="w-3 h-3 inline mr-0.5 -mt-0.5" />
                  {fmtCountdown(liveCountdown(m.seconds_until_next))}
                </span>
                <div className="flex items-center gap-1 shrink-0 w-full lg:w-auto justify-end
                                border-t border-gray-100 dark:border-gray-700/50 pt-2 mt-1
                                lg:border-t-0 lg:pt-0 lg:mt-0">
                  <button className="relative group p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={locked || m.index === 0 || processing === m.message_id}
                    onClick={() => act(m.message_id, () => moveAccountTimer(acc.id, m.message_id, -1), '已移动')}>
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button className="relative group p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={locked || m.index === messages.length - 1 || processing === m.message_id}
                    onClick={() => act(m.message_id, () => moveAccountTimer(acc.id, m.message_id, 1), '已移动')}>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button className="relative group p-1.5 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={m.index !== pointer || processing === m.message_id}
                    onClick={() => act(m.message_id, () => sendAccountTimerNow(acc.id, m.message_id), '已发送')}>
                    <Send className="w-4 h-4" />
                    <HoverTip text="立即发送（仅即将执行的消息）" />
                  </button>
                  <button className="relative group p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={m.index !== pointer || processing === m.message_id}
                    onClick={() => act(m.message_id, () => skipAccountTimer(acc.id, m.message_id), '已跳过')}>
                    <SkipForward className="w-4 h-4" />
                    <HoverTip text="跳过当前待执行消息（指针后移）" />
                  </button>
                  <button className="relative group p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={locked}
                    onClick={() => setEditor({ messageId: m.message_id, text: m.message })}
                    title={locked ? '插件消息由插件托管，不可编辑' : '编辑'}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button className="relative group p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    disabled={locked}
                    onClick={() => setDeleteTarget(m)}>
                    <Trash2 className="w-4 h-4" />
                    <HoverTip text={locked ? '插件消息由插件托管，不可删除' : '删除'} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 添加/编辑弹框 */}
      {editor && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditor(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-5">
            <h3 className="font-semibold mb-3">{editor.messageId ? '编辑定时消息' : '添加定时消息'}</h3>
            <textarea value={editor.text} onChange={(e) => setEditor({ ...editor, text: e.target.value })}
              rows={3} autoFocus className="input w-full" placeholder="输入定时消息内容..." />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setEditor(null)}>取消</Button>
              <Button size="sm" loading={editorSaving} onClick={saveEditor}>
                {editor.messageId ? '保存' : '添加'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认弹框 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="删除定时消息"
        message="确定删除这条定时消息吗？"
        danger
        loading={processing === deleteTarget?.message_id}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setProcessing(deleteTarget.message_id);
          try {
            await deleteAccountTimer(acc.id, deleteTarget.message_id);
            showToast('success', '已删除', '');
            setDeleteTarget(null);
            load();
          } catch (e: any) {
            showToast('error', '删除失败', e.message);
          } finally { setProcessing(''); }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function TimerIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <line x1="10" x2="14" y1="2" y2="2" />
      <line x1="12" x2="15" y1="14" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}

function RadioIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>
  );
}

// 插件 Tab(v1.0.1 卡片样式:底部开关 + 图标按钮组)
// ================================================================== //

const PLUGIN_FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'enabled', label: '已启用' },
  { id: 'disabled', label: '已禁用' },
] as const;
type PluginFilter = (typeof PLUGIN_FILTERS)[number]['id'];

export function PluginsTab({ acc, pluginPageBase, onOpenLibrary, demo = false }: {
  acc: AccountSummary;
  pluginPageBase?: string;
  /** 提供时(管理端 Tab 模式)按钮切换回调;未提供时(账户门户)链接到 /account/library */
  onOpenLibrary?: () => void;
  /** 指引期间为真：不拉账户真实插件，改摆固定的演示清单（见 DEMO_PLUGINS） */
  demo?: boolean;
}) {
  const [plugins, setPlugins] = useState<PluginSummary[]>([]);
  const [libraryVersions, setLibraryVersions] = useState<Record<string, string>>({});
  const demoInstalledNow = useDemoInstalled();
  const [filter, setFilter] = useState<PluginFilter>('all');
  const [kwRaw, setKwRaw] = useState('');

  // ⚠️ 必须放在下方 `if (loading && ...) return` **之前** ——
  // 放进早退之后会让首次渲染少跑一个 hook，React 直接报
  // 「Rendered more hooks than during the previous render」并白屏。
  const visible = useMemo(() => {
    const kw = kwRaw.trim().toLowerCase();
    return plugins.filter((p) => {
      if (filter === 'enabled' && !p.enabled) return false;
      if (filter === 'disabled' && p.enabled) return false;
      if (!kw) return true;
      // 插件名 / 显示名 / 作者 / 简介都搜，中英文混着记的时候不用纠结搜哪个
      return [p.name, p.display_name, p.author, p.short_desc, p.desc]
        .some((s) => (s || '').toLowerCase().includes(kw));
    });
  }, [plugins, filter, kwRaw]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [drawerTarget, setDrawerTarget] = useState<{ name: string; tab?: string } | null>(null);
  const [uninstallTarget, setUninstallTarget] = useState<PluginSummary | null>(null);
  // 一键更新的二次确认：先取预览，拿到「将更新哪些插件 + 版本跨度」再弹框
  // 注意：必须与其他 hook 一起声明在下面的提前 return **之前**，否则 hook 数量不稳定
  const [updateAllConfirm, setUpdateAllConfirm] = useState<{
    message: string;
    groups: BulkGroup[];
  } | null>(null);
  const [updateAllListOpen, setUpdateAllListOpen] = useState(false);
  // 插件日志弹窗：操作失败时自动打开，便于直接看到报错与 traceback
  const [logOpen, setLogOpen] = useState(false);
  const [logHint, setLogHint] = useState('');

  const load = useCallback(async () => {
    // 演示态：不拉账户真实的插件与库版本，直接摆一份固定清单 ——
    // 指引那两步要有东西可指，账户自己装了什么不重要。
    // 不拉库版本 = 卡片上不会出现「可更新」角标，演示态更干净
    if (demo) {
      // 只列「已安装」的演示插件 —— 用户刚在插件库装的那个，切过来就该出现在这儿
      setPlugins(DEMO_PLUGINS.filter((p) => demoInstalled.has(p.name)));
      setLibraryVersions({});
      setLoading(false);
      return;
    }
    try {
      const [installed, library] = await Promise.all([
        fetchAccountPlugins(acc.id),
        fetchAccountLibrary(acc.id).catch(() => []),
      ]);
      setPlugins(installed);
      const versions: Record<string, string> = {};
      for (const p of library) {
        versions[p.name] = p.version;
      }
      setLibraryVersions(versions);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [acc.id]);

  useEffect(() => { load(); }, [load]);

  // 演示态：列表跟着演示的「已安装」走 —— 用户刚在插件库装的插件，切过来就该在这儿
  useEffect(() => {
    if (demo) setPlugins(DEMO_PLUGINS.filter((p) => demoInstalledNow.has(p.name)));
  }, [demo, demoInstalledNow]);

  const act = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setProcessing(key);
    try {
      await fn();
      showToast('success', okMsg, '');
      load();
    } catch (e: any) {
      showToast('error', '操作失败', e.message);
      // 失败时自动打开插件日志，便于直接看到报错与 traceback
      setLogHint(e.message || '');
      setLogOpen(true);
      load();
    } finally { setProcessing(''); }
  };

  // 已安装版本低于插件库版本 → 可更新
  const updateVersion = (p: PluginSummary): string | null => {
    const libV = libraryVersions[p.name];
    if (!libV) return null;
    const a = String(p.version).split('.').map((x) => parseInt(x, 10) || 0);
    const b = libV.split('.').map((x) => parseInt(x, 10) || 0);
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      const x = a[i] ?? 0;
      const y = b[i] ?? 0;
      if (x !== y) return x < y ? libV : null;
    }
    return null;
  };

  const openDrawer = (name: string, tab?: string) => {
    setDrawerTarget({ name, tab });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  const enabledCount = plugins.filter((p) => p.enabled).length;
  /** 已安装版本低于插件库版本的插件——一键更新的目标 */
  const updatable = plugins.filter((p) => updateVersion(p) !== null);

  const askUpdateAll = async () => {
    setProcessing('update-all');
    try {
      const p = await updateAllAccountPlugins(acc.id, true);
      if (p.groups.length === 0) {
        showToast('success', '所有插件均为最新，无需更新', '');
        return;
      }
      setUpdateAllListOpen(false);
      setUpdateAllConfirm({ message: p.message, groups: p.groups });
    } catch (e: any) {
      showToast('error', '无法获取预览', e.message);
    } finally { setProcessing(''); }
  };

  const doUpdateAll = async () => {
    setProcessing('update-all');
    try {
      const r = await updateAllAccountPlugins(acc.id, false);
      showToast('success', r.message || '插件已更新', '');
      setUpdateAllConfirm(null);
      load();
    } catch (e: any) {
      showToast('error', '更新失败', e.message);
    } finally { setProcessing(''); }
  };

  return (
    <div className="space-y-4">
      {demo && <DemoBanner what="演示用插件清单 —— 这里显示的不是你账户里真实安装的插件" />}
      {/* 头部 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">已安装插件</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {plugins.length} 个插件 · {enabledCount} 个已启用
            {updatable.length > 0 && ` · ${updatable.length} 个可更新`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="secondary" icon={<RefreshCw className="w-4 h-4" />}
            disabled={updatable.length === 0 || !!processing}
            loading={processing === 'update-all'}
            onClick={askUpdateAll}>
            一键更新{updatable.length > 0 ? ` (${updatable.length})` : ''}
          </Button>
          {onOpenLibrary ? (
            <Button size="sm" variant="secondary" icon={<Plus className="w-4 h-4" />}
              onClick={onOpenLibrary}>插件库</Button>
          ) : (
            <Link to="/account/library">
              <Button size="sm" variant="secondary" icon={<Plus className="w-4 h-4" />}>插件库</Button>
            </Link>
          )}
        </div>
      </div>

      {/* 筛选 */}
      <div className="space-y-2">
        <SearchInput value={kwRaw} onChange={setKwRaw} placeholder="搜索插件名 / 作者 / 简介…" />
        <FilterChips
          options={PLUGIN_FILTERS.map((f) => ({
            ...f,
            count: f.id === 'all' ? plugins.length : undefined,
          }))}
          value={filter} onChange={setFilter} />
      </div>

      {/* 卡片网格 */}
      {visible.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-10 text-center text-gray-400 text-sm">
            {plugins.length === 0 ? '尚未安装任何插件 —— 请前往插件库安装' : '该分类下暂无插件'}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-guide="pg-list">
          {visible.map((p) => (
            <div key={p.name}
              className="relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col">
              {/* 卡片主体 */}
              <div className="p-5 flex-1">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {/* 有插件主页的整卡可点（拉伸链接：标题是真实 <a>，其 ::after
                          覆盖整卡），没有的就不做成链接、点了没反应 —— 与账户卡片同一套
                          写法，保留键盘可达与中键新标签页打开。
                          卡片上其余交互元素（Button / IconBtn / 开关都自带 relative）
                          会盖在这层之上，不受影响 */}
                      {p.has_ui ? (
                        <Link
                          to={`${pluginPageBase ?? '/account/plugin'}/${p.name}/page`}
                          // 锚点挂在这个 <Link> 上，**不能**挂到外层卡片：卡片的
                          // 拉伸 ::after 虽然让整张卡看着可点，但卡片里还嵌着启用开关
                          // 和那几个图标按钮 —— 锚点落在卡片上，指引给卡片开的那个可点
                          // 的洞就会把它们一起放出去，点一下就真的打接口了。
                          // 挂在这儿，洞就只有插件名那一块，正好是「点它进主页」
                          data-guide={p.has_ui ? 'pg-page' : undefined}
                          data-guide-clickable={demo ? '1' : undefined}
                          // 指引期间**撤掉**那条拉伸覆盖整卡的 ::after：卡片的可点区
                          // 这时只剩蒙版开的那一个洞，拉伸链接不起好作用，只会闯祸 ——
                          // 洞是按矩形算的（比目标外扩一圈），按钮又是圆角，这两处
                          // 漏出去的点全落在它上面：用户点「详情」稍微点歪就进了插件主页
                          className={`group/link inline-flex items-center gap-2 rounded
                                     hover:text-primary-600 dark:hover:text-primary-400
                                     transition-colors focus:outline-none focus-visible:ring-2
                                     focus-visible:ring-primary-500 focus-visible:ring-offset-2
                                     dark:focus-visible:ring-offset-gray-800
                                     ${demo ? '' : 'after:absolute after:inset-0'}`}
                        >
                          <MarqueeText text={p.display_name || p.name} />
                          {/* 「插件主页」这几个字比光一个图标说得清楚：只放图标时，
                              用户仍不知道点进去是什么。字号与颜色都比插件名轻一档，
                              读起来是附属标签而不是标题的一部分。
                              只有 has_ui 的插件才有 —— 卡片刻意不做得「看起来都能点」。 */}
                          <span className="shrink-0 inline-flex items-center gap-0.5
                                           text-[11px] font-normal
                                           text-gray-400 dark:text-gray-500
                                           group-hover/link:text-primary-500">
                            <ExternalLink className="w-3 h-3" />
                            插件主页
                          </span>
                        </Link>
                      ) : (
                        <MarqueeText text={p.display_name || p.name} />
                      )}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-gray-400 dark:text-gray-500">v{p.version}</span>
                      {updateVersion(p) && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium text-[10px] px-1.5 py-0"
                          title={`插件库最新版本 v${updateVersion(p)}`}
                        >
                          <span className="inline-block rounded-full w-1.5 h-1.5 bg-blue-500" />
                          可更新
                        </span>
                      )}
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{p.author}</span>
                    </div>
                  </div>
                  <span className={
                    'inline-flex items-center gap-1.5 rounded-full font-medium text-[10px] px-1.5 py-0 ' +
                    (p.enabled
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                      : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400')
                  }>
                    <span className={`inline-block rounded-full w-1.5 h-1.5 ${p.enabled ? 'bg-emerald-500' : 'bg-surface-400'}`} />
                    {p.enabled ? '已启用' : '未启用'}
                  </span>
                  {p.last_error && (
                    <button
                      title={`初始化失败：${p.last_error}\n点击查看插件日志`}
                      onClick={() => { setLogHint(p.last_error || ''); setLogOpen(true); }}
                      className="relative z-10 inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium
                                 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400
                                 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                      初始化失败
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-1">
                  {p.short_desc || p.desc || '无描述'}
                </p>
                <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate">
                  {p.plugin_id}
                </p>
              </div>

              {/* 底部:开关 + 图标按钮组 */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-xl">
                {/* 用共享开关：样式与全站其它开关一致，不再手抄一遍 class */}
                <Switch
                  checked={p.enabled}
                  loading={processing === p.name}
                  onChange={() => act(p.name, () => p.enabled
                    ? disableAccountPlugin(acc.id, p.name)
                    : enableAccountPlugin(acc.id, p.name),
                    p.enabled ? '已禁用' : '已启用')}
                  hint={p.enabled ? '点击禁用' : '点击启用'}
                  label={`${p.display_name || p.name} 启用`}
                />

                <div className="flex items-center gap-0.5 lg:gap-1 flex-nowrap">
                  {updateVersion(p) && (
                    <IconBtn icon={<Download className="w-3.5 h-3.5" />} label={`更新到 v${updateVersion(p)}`}
                      loading={processing === `update-${p.name}`} disabled={processing === `update-${p.name}`}
                      onClick={() => act(`update-${p.name}`, () => updateAccountPlugin(acc.id, p.name), '已更新到库版本')} />
                  )}
                  <IconBtn icon={<Eye className="w-3.5 h-3.5" />} label="基本信息"
                    data-guide="pg-info" data-guide-clickable={demo ? '1' : undefined}
                    onClick={() => openDrawer(p.name, 'info')} />
                  <IconBtn icon={<SettingsIcon />} label="配置"
                    data-guide="pg-config" data-guide-clickable={demo ? '1' : undefined}
                    onClick={() => openDrawer(p.name, 'config')} />
                  <IconBtn icon={<RefreshCw className="w-3.5 h-3.5" />} label="重载"
                    loading={processing === `reload-${p.name}`} disabled={processing === `reload-${p.name}`}
                    onClick={() => act(`reload-${p.name}`, () => reloadAccountPlugin(acc.id, p.name), '已重载')} />
                  {/* 「插件主页」入口已提升为整卡可点（见卡片标题处的拉伸链接），
                      这里不再重复放按钮 */}
                  <IconBtn icon={<BookOpenIcon />} label="文档" onClick={() => openDrawer(p.name, 'readme')} />
                  <IconBtn icon={<Trash2 className="w-3.5 h-3.5" />} label="卸载" onClick={() => setUninstallTarget(p)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 详情抽屉(账户模式:含配置/权限) */}
      {drawerTarget && (
        <PluginDrawer
          pluginName={drawerTarget.name}
          open
          accountId={acc.id}
          initialTab={drawerTarget.tab}
          // 演示态喂假详情：演示插件名在账户里不存在，照常去拉会 404
          demoDetail={demo ? makeDemoPluginDetail(drawerTarget.name) : null}
          onClose={() => setDrawerTarget(null)}
          onUpdate={load}
        />
      )}

      {/* 卸载确认：三个数据选项默认不勾选（组件内每次打开都会重置） */}
      <UninstallDialog
        open={uninstallTarget !== null}
        pluginName={(uninstallTarget?.display_name || uninstallTarget?.name) ?? ''}
        loading={processing === `del-${uninstallTarget?.name}`}
        onConfirm={async (deleteConfig, deleteData, deletePersistent) => {
          if (!uninstallTarget) return;
          setProcessing(`del-${uninstallTarget.name}`);
          try {
            await uninstallAccountPluginFromAccount(
              acc.id, uninstallTarget.name, deleteConfig, deleteData, deletePersistent,
            );
            showToast('success', '已卸载');
            setUninstallTarget(null);
            load();
          } catch (e: any) {
            showToast('error', '卸载失败', e.message);
          } finally { setProcessing(''); }
        }}
        onCancel={() => setUninstallTarget(null)}
      />

      {/* 插件日志：操作失败时自动打开，也可由「初始化失败」徽标打开 */}
      <PluginLogDialog
        open={logOpen}
        hint={logHint}
        onClose={() => { setLogOpen(false); setLogHint(''); }}
      />

      {/* 一键更新二次确认——会重启插件实例，误触代价大 */}
      {updateAllConfirm && (
        <ConfirmDialog
          open
          title="一键更新插件"
          message={`${updateAllConfirm.message}。更新会重启对应插件实例，`
            + '其插件消息与内部状态会中断（配置与启用状态保留）'}
          confirmLabel="确认更新"
          variant="warning"
          loading={processing === 'update-all'}
          onConfirm={doUpdateAll}
          onCancel={() => setUpdateAllConfirm(null)}>
          <div className="mt-3">
            <button type="button" onClick={() => setUpdateAllListOpen((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-primary-600
                         dark:text-primary-400 hover:underline">
              {updateAllListOpen
                ? <ChevronUp className="w-3.5 h-3.5" />
                : <ChevronDown className="w-3.5 h-3.5" />}
              {updateAllListOpen ? '收起' : '展开'}明细（{updateAllConfirm.groups.length} 个插件）
            </button>
            {updateAllListOpen && (
              <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-200
                              dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                {updateAllConfirm.groups.map((g) => (
                  <div key={g.label} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                      {g.label}
                    </span>
                    <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 shrink-0">
                      {g.items.join('')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}

/** 底部图标按钮(v1.0.1 样式:ghost + 悬浮提示) */
/** `...rest` 透传是为了让操作指引能往上挂 data-guide / data-guide-clickable */
function IconBtn({ icon, label, onClick, loading, disabled, ...rest }: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      onClick={onClick}
      disabled={disabled}
      className="relative group inline-flex items-center justify-center font-medium rounded-lg
                 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
                 disabled:opacity-50 disabled:cursor-not-allowed
                 bg-transparent text-gray-600 dark:text-gray-400
                 hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-400
                 h-8 px-3 text-xs gap-1.5">
      <span className="h-3.5 w-3.5 [&_svg]:h-full [&_svg]:w-full shrink-0">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      </span>
      <HoverTip text={label} />
    </button>
  );
}

function SettingsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function BookOpenIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  );
}

// ================================================================== //
// 插件库 Tab(账户可安装的库插件列表 + 安装)
// 账户持有者门户与管理端账户详情共用。
// ================================================================== //

export function LibraryTab({ acc, demo = false }: {
  acc: AccountSummary;
  /** 指引期间为真：不拉真实插件库，改摆固定的演示清单（见 DEMO_LIBRARY） */
  demo?: boolean;
}) {
  const [library, setLibrary] = useState<LibraryPlugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState('');
  const [detailTarget, setDetailTarget] = useState<{ name: string; tab?: string } | null>(null);
  // 账户级偏好：安装后是否自动启用（默认关闭）
  const [autoEnable, setAutoEnable] = useState(acc.auto_enable_on_install);
  const [savingPref, setSavingPref] = useState(false);
  const [libKw, setLibKw] = useState('');
  const [libFilter, setLibFilter] = useState<'all' | 'installed' | 'notinstalled'>('all');

  const libFiltered = useMemo(() => {
    const kw = libKw.trim().toLowerCase();
    return library.filter((p) => {
      if (libFilter === 'installed' && !p.installed) return false;
      if (libFilter === 'notinstalled' && p.installed) return false;
      if (!kw) return true;
      return [p.name, p.display_name, p.desc, p.author, p.short_desc]
        .some((x) => (x || '').toLowerCase().includes(kw));
    });
  }, [library, libKw, libFilter]);

  const demoInstalledNow = useDemoInstalled();

  const load = useCallback(async () => {
    // 演示态：不拉真实插件库，直接摆一份固定清单 —— 空库会让「插件库」那一步没东西可指。
    // 「是否已安装」读的是演示状态，不是数据里写死的那份
    if (demo) {
      setLibrary(DEMO_LIBRARY.map((p) => ({ ...p, installed: demoInstalled.has(p.name) })));
      setLoading(false);
      return;
    }
    try {
      setLibrary(await fetchAccountLibrary(acc.id));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [acc.id, demo]);

  useEffect(() => { load(); }, [load]);

  // 每次进这一页都把演示进度重置：重看指引时要从「还没装」从头演，
  // 否则上一次装过的还留着，那一步就没得演了。
  // （指引期间导航是挡着的，用户不会中途回到这页，所以不会误reset 掉刚装的）
  useEffect(() => {
    if (demo) setDemoInstalled(new Set(DEMO_INITIAL_INSTALLED));
  }, [demo]);

  // 用户点了「安装」之后，把「已安装」反映到列表上
  useEffect(() => {
    if (!demo) return;
    setLibrary(DEMO_LIBRARY.map((p) => ({ ...p, installed: demoInstalledNow.has(p.name) })));
  }, [demo, demoInstalledNow]);

  /**
   * 刚写入、但父组件的 acc 还没刷新到的值；null 表示没有未决改动。
   *
   * 父组件会（按需或定期）重新拉账户摘要。若在保存在途时拿**旧值**把乐观更新
   * 顶回去，开关就会「开 → 关 → 开」地抽搐 —— 线上就是这么抖的。
   */
  const pendingAutoEnable = useRef<boolean | null>(null);

  // 父组件刷新账户摘要后同步（如切回本 Tab、面板重新拉取）。
  // 有未决写入时先不同步：等父组件真的拉到我们刚写的那个值，再恢复正常同步。
  useEffect(() => {
    if (pendingAutoEnable.current === null) {
      setAutoEnable(acc.auto_enable_on_install);
    } else if (acc.auto_enable_on_install === pendingAutoEnable.current) {
      pendingAutoEnable.current = null;
    }
  }, [acc.auto_enable_on_install]);

  const toggleAutoEnable = async (next: boolean) => {
    pendingAutoEnable.current = next;
    setSavingPref(true);
    setAutoEnable(next); // 乐观更新，失败回滚
    try {
      await updateAccountPreferences(acc.id, { auto_enable_on_install: next });
      showToast('success', next ? '安装插件后将自动启用' : '安装插件后将保持停用', '');
    } catch (e: any) {
      pendingAutoEnable.current = null;
      setAutoEnable(!next);
      showToast('error', '设置失败', e.message);
    } finally { setSavingPref(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {demo && <DemoBanner what="演示用插件库清单 —— 这里显示的不是面板上真实的插件库内容" />}
      {/* 头部 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">插件库</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            安装 = 拷贝源码副本到本账户独立运行 · {library.length} 个插件
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* 开关按下即通过 /accounts/{id}/preferences 落盘（乐观更新，失败回滚），
              无需再点保存；保存期间显示 spinner 并禁用，避免连点 */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300 select-none">
              安装后自动启用
            </span>
            {/* 转圈由 Switch 内部渲染（旋钮里）—— 放在外面会飘在标签与开关之间，
                保存前后还把间距撑得一跳一跳 */}
            <Switch
              checked={autoEnable}
              onChange={toggleAutoEnable}
              loading={savingPref}
              label="安装后自动启用"
              title="开启后，从插件库安装插件会立即启用它；默认关闭（安装后保持停用）。点击即保存"
            />
          </div>
          <Button variant="secondary" icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => { setLoading(true); load(); }}>
            刷新
          </Button>
        </div>
      </div>

      {library.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-10 text-center text-gray-400 text-sm">插件库为空</div>
        </div>
      ) : (
        /* 筛选控件与卡片网格是兄弟节点 —— 三元分支里必须用 Fragment 包住 */
        <>
        <div className="space-y-2">
          <SearchInput value={libKw} onChange={setLibKw} placeholder="搜索插件名 / 作者 / 简介…" />
          <FilterChips
            options={[
              { id: 'all', label: '全部', count: library.length },
              { id: 'installed', label: '已安装',
                count: library.filter((p) => p.installed).length },
              { id: 'notinstalled', label: '未安装',
                count: library.filter((p) => !p.installed).length },
            ] as const}
            value={libFilter} onChange={setLibFilter} />
        </div>

        {libFiltered.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
            没有符合条件的插件
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-guide="lb-list">
          {libFiltered.map((p) => (
            <div key={p.name}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col">
              {/* 卡片主体(v1.0.1 样式) */}
              <div className="p-5 flex-1">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      <MarqueeText text={p.display_name || p.name} />
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-gray-400 dark:text-gray-500">v{p.version}</span>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{p.author}</span>
                    </div>
                  </div>
                  {/* 这个角标是「装好了没」最直白的信号，指引拿它当那一步的完成判据 */}
                  <span data-guide={demo && p.name === DEMO_INSTALL_NAME && p.installed ? 'lb-installed' : undefined}
                    className={
                    'inline-flex items-center gap-1.5 rounded-full font-medium text-[10px] px-1.5 py-0 ' +
                    (p.installed
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                      : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400')
                  }>
                    <span className={`inline-block rounded-full w-1.5 h-1.5 ${p.installed ? 'bg-emerald-500' : 'bg-surface-400'}`} />
                    {p.installed ? '已安装' : '未安装'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-1">
                  {p.short_desc || p.desc || '无描述'}
                </p>
                <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate">
                  {p.plugin_id}
                </p>
              </div>
              {/* 底部操作区 */}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50 rounded-b-xl">
                <Button size="sm" variant="ghost"
                  onClick={() => setDetailTarget({ name: p.name, tab: 'readme' })}>
                  详情
                </Button>
                {!p.installed ? (
                  <Button size="sm" variant="success" icon={<Plus className="w-4 h-4" />}
                    // 指引引导用户装的就是这一个（带插件主页的那个）。
                    // 锚点与可点声明都只挂它一个 —— 别的卡的「安装」仍然点不动
                    data-guide={demo && p.name === DEMO_INSTALL_NAME ? 'lb-install' : undefined}
                    data-guide-clickable={demo && p.name === DEMO_INSTALL_NAME ? '1' : undefined}
                    loading={processing === p.name} disabled={processing === p.name}
                    onClick={async () => {
                      // 演示态只改本地演示状态：不请求、不落盘
                      if (demo) {
                        setDemoInstalled(new Set(demoInstalled).add(p.name));
                        showToast('success', `已安装 ${p.display_name || p.name}（演示，没有真的装）`, '');
                        return;
                      }
                      setProcessing(p.name);
                      try {
                        await installAccountPlugin(acc.id, p.name);
                        showToast('success', `已安装 ${p.display_name || p.name}(默认停用)`, '');
                        load();
                      } catch (e: any) {
                        showToast('error', '安装失败', e.message);
                      } finally { setProcessing(''); }
                    }}>
                    安装
                  </Button>
                ) : (
                  <span className="text-xs text-gray-400">已安装 —— 请在「插件」页面启用与配置</span>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* 插件详情抽屉(库模式:文档/更新日志/使用账户) */}
      {detailTarget && (() => {
        const meta = library.find((p) => p.name === detailTarget.name);
        return (
          <PluginDrawer
            pluginName={detailTarget.name}
            open
            onClose={() => setDetailTarget(null)}
            onUpdate={load}
            initialTab={detailTarget.tab}
            showAccountsTab={false}
            accounts={[{ id: acc.id, name: acc.name }]}
            usedByAccounts={meta?.used_by_accounts ?? []}
            libraryMeta={meta ? {
              name: meta.name,
              display_name: meta.display_name,
              plugin_id: meta.plugin_id,
              version: meta.version,
              has_readme: meta.has_readme,
              has_changelog: meta.has_changelog,
            } : undefined}
          />
        );
      })()}
    </div>
  );
}

// ================================================================== //
// 页面主体
// ================================================================== //

type TabKey = 'overview' | 'live' | 'bot' | 'timer' | 'plugins' | 'library';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: '概览', icon: <BotIcon className="w-4 h-4" /> },
  { key: 'live', label: '直播间', icon: <Radio className="w-4 h-4" /> },
  { key: 'bot', label: 'Bot', icon: <BotIcon className="w-4 h-4" /> },
  { key: 'timer', label: '定时消息', icon: <Clock className="w-4 h-4" /> },
  { key: 'plugins', label: '插件', icon: <Puzzle className="w-4 h-4" /> },
  { key: 'library', label: '插件库', icon: <Puzzle className="w-4 h-4" /> },
];

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const accountId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const [acc, setAcc] = useState<AccountSummary | null>(null);
  // Tab 与 URL 同步(?tab=...),插件主页等返回时可直达对应标签页
  const [tab, setTab] = useState<TabKey>(() => {
    const t = searchParams.get('tab');
    return TABS.some((x) => x.key === t) ? (t as TabKey) : 'overview';
  });
  const [loading, setLoading] = useState(true);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewMode, setRenewMode] = useState<'days' | 'code' | 'set' | 'permanent' | 'expire'>('days');
  const [renewing, setRenewing] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoBusy, setInfoBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setAcc(await fetchAccountSummary(accountId));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // 浏览器前进/后退等外部 URL 变化时同步 Tab 状态
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((x) => x.key === t) && t !== tab) {
      setTab(t as TabKey);
    }
    /* eslint-disable-next-line */
  }, [searchParams]);

  const switchTab = (key: TabKey) => {
    setTab(key);
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', key);
    }
    setSearchParams(next, { replace: true });
  };

  if (loading || !acc) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        {!loading && <p className="text-sm text-gray-400">账户不存在</p>}
        <Link to="/" className="flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <ArrowLeft className="w-4 h-4" /> 返回账户总览
        </Link>
      </div>
    );
  }

  const handleExpire = async (aid: number) => {
    setRenewing(true);
    try {
      const res = await expireAccount(aid);
      showToast('success', res.notice || '已设为过期停用', '');
      setRenewOpen(false);
      load();
    } catch (e: any) {
      showToast('error', '设置失败', e.message);
    } finally { setRenewing(false); }
  };

  const handleSaveInfo = async (data: {
    name: string;
    remark: string;
    expiry_notice_enabled: boolean;
  }) => {
    if (!acc) return;
    setInfoBusy(true);
    try {
      setAcc(await updateAccount(acc.id, data));
      showToast('success', '账户资料已更新', '');
      setInfoOpen(false);
    } catch (e: any) {
      showToast('error', '保存失败', e.message);
    } finally {
      setInfoBusy(false);
    }
  };

  const handleRenew = async (aid: number, data: RenewRequest) => {
    setRenewing(true);
    try {
      const res = await renewAccount(aid, data);
      if (res.notice) {
        // 后端判定该操作无意义（如永久账户叠加续期）——如实告知而非谎报成功
        showToast('info', '未做变更', res.notice);
      } else {
        showToast('success', '续期成功', '');
      }
      setRenewOpen(false);
      load();
    } catch (e: any) {
      showToast('error', '续期失败', e.message);
    } finally { setRenewing(false); }
  };

  const handleRedeem = async (aid: number, code: string) => {
    setRenewing(true);
    try {
      await redeemAccount(aid, code);
      showToast('success', '兑换成功', '');
      setRenewOpen(false);
      load();
    } catch (e: any) {
      showToast('error', '兑换失败', e.message);
    } finally { setRenewing(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      {/* 头部 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* 每一层都带 min-w-0：flex 子项默认 min-width:auto，少了它这条链就缩不动，
            长名字会把徽章挤走或自己折行。加上之后名字能在剩余宽度里滚动 */}
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-2xl font-bold min-w-0">
                <MarqueeText text={acc.name} />
              </h1>
              <span className={
                'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ' +
                (acc.bot_public
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300')
              }>
                {acc.bot_public ? '公共 Bot' : '私有 Bot'}
              </span>
              <ExpiryBadge expiresAt={acc.expires_at} pausedReason={acc.paused_reason} />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              直播间 {acc.room_id ?? '未绑定'} · 插件 {acc.enabled_plugin_count}/{acc.plugin_count}
            </p>
            {acc.remark && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 truncate"
                title={acc.remark}>
                备注：{acc.remark}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="secondary" icon={<Pencil className="w-4 h-4" />}
            onClick={() => setInfoOpen(true)}>编辑资料</Button>
          <Button size="sm" variant="secondary" icon={<KeyRound className="w-4 h-4" />}
            onClick={() => { setRenewMode('code'); setRenewOpen(true); }}>兑换</Button>
          <Button size="sm" variant="secondary" icon={<Hourglass className="w-4 h-4" />}
            onClick={() => { setRenewMode('set'); setRenewOpen(true); }}>设置剩余</Button>
          <Button size="sm" icon={<CalendarClock className="w-4 h-4" />}
            onClick={() => { setRenewMode('days'); setRenewOpen(true); }}>续期</Button>
        </div>
      </div>

      {/* Tabs(移动端均分,无横向滚动条)
          六个 Tab 均分窄屏宽度后每个只有 60px 上下，图标+四字标签放不下，
          标签会在按钮**内部**折成两行 —— 之前看到的「双行」就是这个。
          移动端隐藏图标把宽度让给文字，六个入口全部保留可见（比横向滚动藏起
          一部分更适合导航）；标签加 nowrap 兜底，宁可略挤也不折行。 */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => switchTab(t.key)}
            className={
              'flex-1 min-w-0 flex items-center justify-center gap-1 sm:gap-1.5 px-0.5 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 -mb-px transition-colors ' +
              (tab === t.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300')
            }>
            <span className="hidden sm:flex shrink-0">{t.icon}</span>
            <span className="whitespace-nowrap">{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab acc={acc} onRenew={(m) => { setRenewMode(m); setRenewOpen(true); }} />}
      {/* 把账户概要认真刷新交给 LiveTab：绑定/更换/解绑都会改 `acc.room_id`，
          而它本身是挂载时取的快照，不刷新就会拿着旧值参与渲染判断 */}
      {tab === 'live' && <LiveTab acc={acc} onAccountReload={load} />}
      {tab === 'bot' && <BotTab acc={acc} onAccountChanged={load} />}
      {tab === 'timer' && <TimerTab acc={acc} />}
      {tab === 'plugins' && <PluginsTab acc={acc} pluginPageBase={`/account/${acc.id}/plugin`} onOpenLibrary={() => switchTab('library')} />}
      {tab === 'library' && <LibraryTab acc={acc} />}

      <RenewDialog
        open={renewOpen}
        accountId={acc.id}
        accountName={acc.name}
        mode={renewMode}
        loading={renewing}
        onRenew={handleRenew}
        onRedeem={handleRedeem}
        onExpire={handleExpire}
        onCancel={() => setRenewOpen(false)}
      />
      <AccountInfoDialog
        open={infoOpen}
        account={acc}
        loading={infoBusy}
        onSave={handleSaveInfo}
        onCancel={() => setInfoOpen(false)}
      />
    </div>
  );
}
